import type { Organization, Practitioner, Reference } from '@medplum/fhirtypes'
import { ICD10_SYSTEM, SNOMED_CT_SYSTEM } from '@seen/core/codeSystems'
import {
  type ConditionTemplate,
  normalizeConditionTemplateTitle,
  type OrganizationConditionEntry,
  type OrganizationConditionsList,
} from '@seen/core/models/organizationConditionsList'
import { getReferenceString } from '@seen/core/references'
import { Button } from '@seen/ui'
import { type JSX, useEffect } from 'react'
import { z } from 'zod'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormErrorMessage } from '#src/components/core/form/FormErrorMessage.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

const conditionTemplateSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  icd10Code: z.string().min(1, 'ICD-10 code is required'),
  icd10Display: z.string(),
  snomedCode: z.string(),
  snomedDisplay: z.string(),
})

type ConditionTemplateFormValues = z.infer<typeof conditionTemplateSchema>

function toDefaultValues(
  template: ConditionTemplate | null
): ConditionTemplateFormValues {
  if (!template) {
    return {
      title: '',
      icd10Code: '',
      icd10Display: '',
      snomedCode: '',
      snomedDisplay: '',
    }
  }
  return {
    title: template.title,
    icd10Code: template.icd10Codes[0]?.code ?? '',
    icd10Display: template.icd10Codes[0]?.display ?? '',
    snomedCode: template.snomedCodes[0]?.code ?? '',
    snomedDisplay: template.snomedCodes[0]?.display ?? '',
  }
}

export interface ConditionTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  conditionsList: OrganizationConditionsList
  // When provided, the dialog opens in edit mode for this entry; its index
  // identifies the List row to replace.
  template?: OrganizationConditionEntry | null
  isReadOnly: boolean
}

export function ConditionTemplateDialog({
  open,
  onOpenChange,
  conditionsList,
  template,
  isReadOnly,
}: ConditionTemplateDialogProps): JSX.Element {
  const medplum = useMedplum()
  const patchMutation = usePatchMutation<OrganizationConditionsList>()
  const editingTemplate = template ?? null

  const form = useForm({
    schema: conditionTemplateSchema,
    defaultValues: toDefaultValues(editingTemplate),
  })

  // Reset form values whenever the dialog opens or the target template changes.
  useEffect(() => {
    if (open) {
      form.reset(toDefaultValues(editingTemplate))
    }
  }, [open, editingTemplate, form])

  const submit = form.handleSubmit((values) => {
    // add() silently no-ops on a duplicate title; surface it as a form error
    // instead of closing the dialog without adding anything.
    if (!editingTemplate) {
      const normalizedTitle = normalizeConditionTemplateTitle(values.title)
      const isDuplicate = conditionsList.conditions.some(
        (existing) =>
          normalizeConditionTemplateTitle(existing.title) === normalizedTitle
      )
      if (isDuplicate) {
        form.setError('title', {
          message: 'A condition with this name already exists',
        })
        return
      }
    }

    const profile = medplum.getProfile()
    const fallbackRef: Reference<Practitioner | Organization> = {
      reference: 'Organization/seen-health',
    }
    const addedByRef: Reference<Practitioner | Organization> = profile
      ? { reference: getReferenceString(profile) }
      : fallbackRef

    const now = new Date().toISOString()
    const snomedCode = values.snomedCode.trim()
    const icd10Code = values.icd10Code.trim()
    const icd10Display = values.icd10Display.trim()
    const snomedDisplay = values.snomedDisplay.trim()

    // 'custom'/'imported' mark how the entry entered the List and are
    // preserved; otherwise a SNOMED-bearing admin edit stamps source='manual'.
    // verifiedRoundtripIcd10 must also be set so resolve() treats this as a
    // trusted mapping — an admin asserting "this SNOMED is correct for this
    // ICD-10" is equivalent to a verified round-trip.
    let source = editingTemplate?.source
    if (source !== 'custom' && source !== 'imported' && snomedCode) {
      source = 'manual'
    }

    const updatedTemplate: ConditionTemplate = {
      title: values.title.trim(),
      icd10Codes: [
        {
          system: ICD10_SYSTEM,
          code: icd10Code,
          ...(icd10Display && { display: icd10Display }),
        },
      ],
      snomedCodes: snomedCode
        ? [
            {
              system: SNOMED_CT_SYSTEM,
              code: snomedCode,
              ...(snomedDisplay && { display: snomedDisplay }),
            },
          ]
        : [],
      addedOn: editingTemplate?.addedOn ?? now,
      addedBy: editingTemplate?.addedBy ?? addedByRef,
      ...(source && { source }),
      ...(snomedCode && {
        verifiedAt: now,
        verifiedRoundtripIcd10: icd10Code,
      }),
    }

    patchMutation.mutate(
      {
        instance: conditionsList,
        mutateFn: (list) => {
          if (editingTemplate) {
            // Replace in place: remove-then-add would hit add()'s title-dedup
            // guard and silently drop the entry whenever the new title matches
            // another entry (titles are not unique in the list).
            list.replaceByIndex(editingTemplate.index, updatedTemplate)
          } else {
            list.add(updatedTemplate)
          }
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  })

  const isAdding = !editingTemplate

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay notScrollable>
        <div className="flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {isAdding ? 'Add Condition' : 'Edit Condition'}
            </DialogTitle>
          </DialogHeader>
          <Form
            onSubmit={submit}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <DialogContent className="space-y-4 min-h-0 flex-1 overflow-y-auto">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <div className="space-y-1.5">
                    <FormLabel>Name</FormLabel>
                    <Input
                      {...field}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Condition name"
                      autoFocus
                      disabled={isReadOnly}
                    />
                    <FormErrorMessage />
                  </div>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="icd10Code"
                  render={({ field }) => (
                    <div className="space-y-1.5">
                      <FormLabel>ICD-10 Code</FormLabel>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="e.g. E11.9"
                        className="font-mono"
                        disabled={isReadOnly}
                      />
                      <FormErrorMessage />
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="icd10Display"
                  render={({ field }) => (
                    <div className="space-y-1.5">
                      <FormLabel>ICD-10 Display (optional)</FormLabel>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder="Description"
                        disabled={isReadOnly}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-medium">SNOMED Mapping</p>
                <p className="text-xs text-muted-foreground">
                  Set the IMO-accepted SNOMED code for Elation sync. Admin edits
                  are stamped as manual source.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="snomedCode"
                    render={({ field }) => (
                      <div className="space-y-1.5">
                        <FormLabel>SNOMED Code</FormLabel>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="e.g. 44054006"
                          className="font-mono"
                          disabled={isReadOnly}
                        />
                      </div>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="snomedDisplay"
                    render={({ field }) => (
                      <div className="space-y-1.5">
                        <FormLabel>SNOMED Display (optional)</FormLabel>
                        <Input
                          {...field}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="Description"
                          disabled={isReadOnly}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </DialogContent>
            <DialogFooter className="sticky bottom-0 gap-2 border-t bg-background">
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={patchMutation.isPending}
                >
                  Cancel
                </Button>
              </DialogClose>
              <SubmitButton
                submitting={patchMutation.isPending}
                disabled={isReadOnly}
              >
                {isAdding ? 'Add' : 'Update'}
              </SubmitButton>
            </DialogFooter>
          </Form>
        </div>
      </DialogDisplay>
    </Dialog>
  )
}
